import { type EvalStatus, STATUS_CONFIG, scoreColor, scoreHex } from '@workspace/seedforge-shared'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { Mic } from 'lucide-react'
import { useState } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import { Prose } from '../../components/prose'

interface TrafficLightParam {
  key: string
  label: string
  icon: React.ReactNode
  status: EvalStatus
}

export type NextMoveSeverity = 'critical' | 'high' | 'low'

export interface NextMove {
  text: string
  severity?: NextMoveSeverity
  category?: string
}

export interface ScoreHistoryPoint {
  date: string
  score: number
  categories?: Record<string, number>
}

export interface VerdictHeroProps {
  /** Small label above the summary, e.g. "How SeedForge sees you" */
  label: string
  /** Timestamp shown after the label */
  timestamp?: string
  /** The main summary text displayed as the dominant heading */
  summary: string
  /** Overall verdict label, e.g. "Conditional Invest" */
  verdict: string
  /** Verdict color tier */
  verdictStatus: EvalStatus
  /** Traffic-light parameters */
  parameters: TrafficLightParam[]
  /** Current startup score */
  score: number
  /** Score history across pitch sessions */
  scoreHistory: ScoreHistoryPoint[]
  /** Next moves the founder should take */
  nextMoves: NextMove[]
  /** Invoked when the "Start AI Session" CTA is clicked */
  onStartSession: () => void
  /** Link target for "Your Next Moves" header — leads to full evaluation */
  evaluationTo?: string
  /** Optional banner rendered above the hero content */
  banner?: React.ReactNode
  /** Optional founder-privacy note, rendered on the top row alongside the
   *  Start AI session CTA. */
  privacyNote?: React.ReactNode
  /**
   * Optional primary action rendered before Share and "Start AI session". When
   * set, it owns the orange CTA and "Start AI session" drops to secondary.
   */
  actions?: React.ReactNode
}

const VERDICT_TAG: Record<EvalStatus, string> = {
  strong: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-400',
  moderate: 'border-amber-400/30 bg-amber-400/10 text-amber-400',
  weak: 'border-red-400/30 bg-red-400/10 text-red-400',
}

const CATEGORY_COLORS: Record<string, string> = {
  team: '#a78bfa',
  traction: '#f97316',
  problem_solution: '#38bdf8',
  market: '#34d399',
  vision: '#facc15',
}

const INITIAL_MOVES_COUNT = 5

/* ------------------------------------------------------------------ */
/*  Score Chart                                                        */
/* ------------------------------------------------------------------ */

interface CategoryDef {
  key: string
  label: string
}

/** Compute nice Y-axis range from data. */
function computeYRange(history: ScoreHistoryPoint[], categories?: CategoryDef[]) {
  const allScores: number[] = []
  for (const p of history) {
    allScores.push(p.score)
    if (categories && p.categories) {
      for (const cat of categories) {
        const v = p.categories[cat.key]
        if (v != null) allScores.push(v)
      }
    }
  }
  if (allScores.length === 0) return { min: 0, max: 100, ticks: [0, 25, 50, 75, 100] }

  const dataMin = Math.min(...allScores)
  const dataMax = Math.max(...allScores)

  const yMin = Math.max(0, Math.floor((dataMin - 5) / 10) * 10)
  const yMax = Math.min(100, Math.ceil((dataMax + 5) / 10) * 10)

  const range = yMax - yMin
  const step = range <= 20 ? 5 : range <= 40 ? 10 : 25
  const ticks: number[] = []
  for (let t = yMin; t <= yMax; t += step) ticks.push(t)
  if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax)

  return { min: yMin, max: yMax, ticks }
}

/** Custom tooltip matching app design. */
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{
    dataKey: string
    value: number
    color: string
    name: string
    payload?: { _center?: boolean }
  }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  // Hide tooltip on edge points (used for single-point full-width line)
  if (!payload[0]?.payload?._center) return null

  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-md">
      <div className="space-y-0.5">
        <div className="text-muted-foreground">{label}</div>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-muted-foreground">{entry.name}</span>
            <span className="ml-auto font-medium text-foreground">{entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Dot renderer that only draws on _center entries (hides edge duplicates for single-point charts). */
function CenterDot({
  cx,
  cy,
  payload,
  r = 4,
  stroke = '#888',
  strokeWidth = 2,
}: {
  cx?: number
  cy?: number
  payload?: { _center?: boolean }
  r?: number
  stroke?: string
  strokeWidth?: number
}) {
  if (!payload?._center || cx == null || cy == null) return <g />
  return <circle cx={cx} cy={cy} r={r} fill="#1c1c1c" stroke={stroke} strokeWidth={strokeWidth} />
}

function ScoreChart({
  history,
  categories,
}: {
  history: ScoreHistoryPoint[]
  categories?: CategoryDef[]
}) {
  const hasCategories = categories && categories.length > 0 && history.some((p) => p.categories)
  const [mode, setMode] = useState<'total' | 'categories'>('total')

  if (history.length === 0) return null

  const { min: yMin, max: yMax, ticks: yTicks } = computeYRange(history, categories)
  const lastColor = scoreHex(history[history.length - 1]?.score ?? 0)

  const isSinglePoint = history.length === 1

  // Build chart data — each entry has date, score, and category scores
  // For a single point, create 3 entries (left edge, center with label, right edge)
  // so Recharts draws a full-width line with a single dot in the center
  const buildEntry = (p: ScoreHistoryPoint, date: string) => ({
    date,
    score: p.score,
    _center: false,
    ...Object.fromEntries(categories?.map((cat) => [cat.key, p.categories?.[cat.key] ?? 0]) ?? []),
  })

  // Deduplicate date labels so Recharts tooltip maps to the correct data point
  const deduplicatedDates = history.map((p) => p.date)
  const dateCounts = new Map<string, number>()
  for (let i = 0; i < deduplicatedDates.length; i++) {
    const date = deduplicatedDates[i] ?? ''
    const count = dateCounts.get(date) ?? 0
    if (count > 0) deduplicatedDates[i] = `${date} (${count + 1})`
    dateCounts.set(date, count + 1)
  }

  const chartData =
    isSinglePoint && history[0]
      ? [
          { ...buildEntry(history[0], ''), _center: false },
          { ...buildEntry(history[0], history[0].date), _center: true },
          { ...buildEntry(history[0], ''), _center: false },
        ]
      : history.map((p, i) => ({ ...buildEntry(p, deduplicatedDates[i] ?? p.date), _center: true }))

  return (
    <div>
      {/* Toggle */}
      {hasCategories && (
        <div className="mb-2 flex gap-3">
          <button
            type="button"
            className={cn(
              'pb-0.5 text-xs font-medium transition-colors',
              mode === 'total'
                ? 'border-b border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground/70'
            )}
            onClick={() => setMode('total')}
          >
            Total
          </button>
          <button
            type="button"
            className={cn(
              'pb-0.5 text-xs font-medium transition-colors',
              mode === 'categories'
                ? 'border-b border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground/70'
            )}
            onClick={() => setMode('categories')}
          >
            By Metric
          </button>
        </div>
      )}

      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="currentColor"
            className="text-border/30"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#666' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            ticks={yTicks}
            tick={{ fontSize: 10, fill: '#555' }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            content={<ChartTooltip />}
            cursor={{
              stroke: 'currentColor',
              strokeDasharray: '3 3',
              className: 'text-muted-foreground/40',
            }}
          />

          {mode === 'total' ? (
            <>
              <defs>
                <linearGradient id="score-area-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lastColor} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={lastColor} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="score"
                name="Total"
                stroke={lastColor}
                strokeWidth={2}
                fill="url(#score-area-grad)"
                dot={<CenterDot r={4} stroke={lastColor} strokeWidth={2} />}
                activeDot={<CenterDot r={5} stroke={lastColor} strokeWidth={2} />}
              />
            </>
          ) : (
            categories?.map((cat) => (
              <Line
                key={cat.key}
                type="monotone"
                dataKey={cat.key}
                name={cat.label}
                stroke={CATEGORY_COLORS[cat.key] ?? '#888'}
                strokeWidth={2}
                strokeOpacity={0.8}
                dot={
                  <CenterDot r={3} stroke={CATEGORY_COLORS[cat.key] ?? '#888'} strokeWidth={1.5} />
                }
                activeDot={
                  <CenterDot r={4} stroke={CATEGORY_COLORS[cat.key] ?? '#888'} strokeWidth={2} />
                }
              />
            ))
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Category legend */}
      {mode === 'categories' && categories && (
        <div className="mt-1 flex flex-wrap gap-3">
          {categories.map((cat) => (
            <div key={cat.key} className="flex items-center gap-1.5">
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: CATEGORY_COLORS[cat.key] }}
              />
              <span className="text-[10px] text-muted-foreground">{cat.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Next Moves Panel                                                    */
/* ------------------------------------------------------------------ */

function NextMovesPanel({ moves, linkTo }: { moves: NextMove[]; linkTo?: string }) {
  const totalCount = moves.length

  return (
    <div className="flex flex-col rounded-xl border border-border/60 bg-zinc-900/60 p-5">
      {linkTo ? (
        <a
          href={linkTo}
          className="mb-3.5 flex items-center justify-between transition-colors hover:opacity-80"
        >
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Your Next Moves
          </h3>
          <span className="text-[11px] text-muted-foreground/50">{totalCount} items →</span>
        </a>
      ) : (
        <div className="mb-3.5 flex items-center justify-between">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Your Next Moves
          </h3>
          <span className="text-[11px] text-muted-foreground/50">{totalCount} items</span>
        </div>
      )}

      <div className="divide-y divide-border/40 overflow-y-auto pr-1" style={{ maxHeight: 320 }}>
        {moves.map((move, i) => (
          <div key={move.text} className="flex items-start gap-2.5 py-2.5">
            <span
              className={cn(
                'flex size-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                i < INITIAL_MOVES_COUNT
                  ? 'bg-[#f97316]/10 text-[#f97316]'
                  : 'bg-zinc-800 text-muted-foreground/50'
              )}
            >
              {i + 1}
            </span>
            <div className="flex flex-1 flex-col gap-1">
              <Prose className="text-[13px] leading-snug text-foreground">{move.text}</Prose>
              {move.category && (
                <span className="inline-flex w-fit rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground/50">
                  {move.category}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  VerdictHero                                                        */
/* ------------------------------------------------------------------ */

export { VerdictHero as EvaluationVerdictHero }

export function VerdictHero({
  label,
  timestamp,
  summary,
  verdict,
  verdictStatus,
  parameters,
  score,
  scoreHistory,
  nextMoves,
  onStartSession,
  evaluationTo,
  banner,
  privacyNote,
  actions,
}: VerdictHeroProps) {
  // Derive percentile from score (approximate — top (100 - score)%)
  const percentile = Math.max(1, 100 - score)

  const ctaGroup = (
    <div className="ml-auto flex items-center gap-2">
      {actions}
      {actions ? (
        <SecondaryButton size="sm" icon={<Mic />} onClick={onStartSession}>
          Start AI session
        </SecondaryButton>
      ) : (
        <CtaButton size="sm" icon={<Mic />} onClick={onStartSession}>
          Start AI session
        </CtaButton>
      )}
    </div>
  )

  return (
    <div className="-mx-8 -mt-6 px-8 pb-8 pt-6">
      <div className="mx-auto max-w-6xl">
        {/* Privacy note + CTAs on one row, at the very top — same level, not
            buried down by the traffic lights. */}
        <div className="mb-3 flex flex-wrap items-center gap-3">
          {privacyNote}
          {ctaGroup}
        </div>

        {banner}
        {/* Label + Timestamp */}
        <div className="mb-2 flex items-center gap-3">
          <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground/80">
            {label}
          </p>
          {timestamp && (
            <>
              <span className="text-xs text-muted-foreground/80">&middot;</span>
              <span className="text-xs text-muted-foreground/80">{timestamp}</span>
            </>
          )}
        </div>

        {/* Summary heading */}
        <Prose className="max-w-4xl text-2xl font-semibold leading-snug tracking-tight text-foreground">
          {summary}
        </Prose>
      </div>

      {/* Traffic lights — same content column as the header above, so the
          legend lines up with the title and score instead of the page edge. */}
      <div className="mx-auto max-w-6xl">
        <div className="mt-4 flex flex-wrap items-center gap-5">
          {parameters.map((p) => {
            const config = STATUS_CONFIG[p.status]
            return (
              <button
                key={p.key}
                type="button"
                className="flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-70"
                onClick={() => {
                  document
                    .getElementById(`category-${p.key}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                <span className={cn('size-2 rounded-full', config.dot)} />
                <span className="text-muted-foreground/80">{p.icon}</span>
                <span className="text-sm text-muted-foreground">{p.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="mx-auto max-w-6xl">
        {/* Two-panel layout: Score Card + Next Moves */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* ---- Left: Score Card ---- */}
          <div>
            {/* Top row: Score + Verdict badge */}
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="flex items-baseline gap-2">
                  <span
                    className={cn(
                      'text-5xl font-bold tabular-nums tracking-tight',
                      scoreColor(score)
                    )}
                  >
                    {score}
                  </span>
                  <span className="text-sm text-muted-foreground/60">/ 100</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground/60">
                  Top {percentile}% of startups evaluated
                </p>
              </div>
              <div className="text-right">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                    VERDICT_TAG[verdictStatus]
                  )}
                >
                  <span
                    className={cn(
                      'size-1.5 rounded-full',
                      verdictStatus === 'strong'
                        ? 'bg-emerald-400'
                        : verdictStatus === 'moderate'
                          ? 'bg-amber-400'
                          : 'bg-red-400'
                    )}
                  />
                  {verdict}
                </span>
              </div>
            </div>

            {/* Chart */}
            <ScoreChart
              history={scoreHistory}
              categories={parameters.map((p) => ({ key: p.key, label: p.label }))}
            />
          </div>

          {/* ---- Right: Next Moves ---- */}
          <NextMovesPanel moves={nextMoves} linkTo={evaluationTo} />
        </div>
      </div>
    </div>
  )
}
